import PropTypes from 'prop-types';
import { Avatar, Box, Card, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../../../components/Iconify';
import { EmptyModuleState, LoadingPanel, SectionError } from './ManageUserDialogStates';
import { fDateTime } from '../../../utils/formatTime';

function DeviceDetail({ label, value }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value || 'N/A'}
      </Typography>
    </Stack>
  );
}

DeviceDetail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function DevicesTab({ state }) {
  if (state.loading && !state.loadComplete) {
    return <LoadingPanel />;
  }

  if (state.error && !state.loadComplete) {
    return <SectionError message={state.error} />;
  }

  const devices = state.devices || [];

  if (!devices.length) {
    return (
      <EmptyModuleState
        title="No devices found"
        description="The loaded user detail payload did not include any device observations."
        icon="solar:smartphone-2-bold"
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      {devices.map((device, index) => (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }} key={device.id || device.device_id || index}>
          <Stack spacing={2.5} sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.06),
                    color: 'text.primary',
                  }}
                >
                  <Iconify icon={device.platform === 'android' ? 'solar:smartphone-2-bold' : 'solar:laptop-bold'} width={18} height={18} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">{device.device_model || 'Unknown device'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[device.manufacturer, device.platform ? `${device.platform} ${device.os_version || ''}`.trim() : null]
                      .filter(Boolean)
                      .join(' • ') || 'No platform metadata'}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                <Chip
                  size="small"
                  label={device.is_emulator === '1' ? 'Emulator' : 'Physical device'}
                  color={device.is_emulator === '1' ? 'default' : 'primary'}
                  variant={device.is_emulator === '1' ? 'outlined' : 'filled'}
                />
                <Chip
                  size="small"
                  label={device.is_rooted === '1' ? 'Rooted' : 'Not rooted'}
                  variant="outlined"
                />
              </Stack>
            </Stack>

            <Divider />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={4}>
                <DeviceDetail label="App version" value={device.app_version ? `${device.app_version} (${device.build_number || 'n/a'})` : device.build_number} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DeviceDetail label="Device ID" value={device.device_id} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DeviceDetail label="Install ID" value={device.install_id} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DeviceDetail label="First seen" value={device.first_seen ? fDateTime(device.first_seen) : 'N/A'} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DeviceDetail label="Last seen" value={device.last_seen ? fDateTime(device.last_seen) : 'N/A'} />
              </Grid>
              <Grid item xs={12} md={4}>
                <DeviceDetail
                  label="Fingerprint"
                  value={device.fingerprint_hash ? `${device.fingerprint_hash.slice(0, 12)}...${device.fingerprint_hash.slice(-8)}` : 'N/A'}
                />
              </Grid>
            </Grid>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

DevicesTab.propTypes = {
  state: PropTypes.object.isRequired,
};
